import React, { Dispatch, useState } from 'react'
import { IFile, IInfrastructureProjectObject, IStreet } from '../../../types/project.types'
import generalClasses from '../tabs/styles.module.scss';
import classes from './styles.module.scss';
import { PlusIcon, XIcon } from '../../../assets/icons';
import { convertFilesToImageDate, findOptionByValue, t } from '../../../helpers/utils';
import { createStreet, deleteStreet, updateStreets } from '../../../requests/supervisor.request';
import { useParams } from 'react-router-dom';
import Field from '../../../components/field/field.component';
import ReactSelect from 'react-select';
import { conditionOptions, yesOrNo } from '../../../consts/project.const';
import StreetDocumentation from './street-documentation.component';
import ImageGrid from './image-grid.component';

const baseClass = 'street-list';
const translationKey = 'projects.infrastructure-projects.street';

interface IProps {
    data: IInfrastructureProjectObject;
    list: IStreet[],
    setFormData: Dispatch<React.SetStateAction<IInfrastructureProjectObject>>
    suffix: 'Ru' | 'Kz'
    isAdding: boolean
    setIsAdding: Dispatch<React.SetStateAction<boolean>>
    newStreet: IStreet | null
    setNewStreet: Dispatch<React.SetStateAction<IStreet | null>>
    id: number | null
    isAddingFund: Record<number, boolean>,
    setIsAddingFund: Dispatch<React.SetStateAction<Record<number, boolean>>>,
    newFund: Record<number, { labelRu: string; labelKz: string; value: string }>,
    setNewFund: Dispatch<React.SetStateAction<Record<number, { labelRu: string; labelKz: string; value: string }>>>,
    parentErrors?: Record<string, boolean>,
}

const StreetList = ({
    list, setFormData, suffix, isAdding, setIsAdding, newStreet, setNewStreet,
    id, parentErrors, data, isAddingFund, setIsAddingFund, newFund, setNewFund
}: IProps) => {
    const { tab, kato } = useParams<{ tab: string, kato: string }>();
    const type = tab === 'VILLAGE_ROAD' ? 'VILLAGE_ROAD_STREET' : 'LIGHTING_STREET';
    const lang = suffix.toLowerCase() as 'ru' | 'kz';

    const [errors, setErrors] = useState<Record<string, boolean>>({});

    const updateStreetList = (cb: (prev: IStreet[]) => IStreet[]) =>
        setFormData(prev => ({
            ...prev,
            infrastructureEntity: {
                ...prev.infrastructureEntity,
                streetList: cb(prev.infrastructureEntity.streetList || [])
            }
        }));

    const handleChangeField = (field: string, value: any, streetId: number) => {
        updateStreetList(list => list.map(s => s.id === streetId ? { ...s, [field]: value } : s));
    };

    const updateStreetFiles = (streetId: number, newFiles: IFile[]) => {
        updateStreetList(list =>
            list.map(s => s.id === streetId ? { ...s, files: newFiles } : s)
        );
    };

    const handleAddStreet = () => {
        if (!id || !tab) return;
        setIsAdding(true);
        createStreet(id, type).then(res => setNewStreet(res));
    };

    const saveNewStreet = () => {
        if (!newStreet || !validateNewStreet(newStreet)) return;
        updateStreets(type, [...list, newStreet]).then(() => {
            updateStreetList(prev => [...prev, newStreet]);
            setNewStreet(null);
            setIsAdding(false);
        });
    };

    const cancelAddStreet = () => {
        if (!newStreet) return;
        deleteStreet(newStreet.id, type).then(() => {
            setNewStreet(null);
            setIsAdding(false);
        });
    };

    const deleteStreetById = (streetId: number) => {
        if (!id || !tab) return;
        deleteStreet(streetId, type).then(() => {
            updateStreetList(list => list.filter(s => s.id !== streetId));
        });
    };

    const validateNewStreet = (street: IStreet) => {
        const newErrors: Record<string, boolean> = {};
        if (!street[`name${suffix}`]) {
            newErrors[`name${suffix}`] = true;
        }
        if (street.totalStreetLength === undefined || street.totalStreetLength === null) {
            newErrors.totalStreetLength = true;
        }
        if (!street.factCondition) {
            newErrors.factCondition = true;
        }
        if (!street.repairRequired) {
            newErrors.repairRequired = true;
        }
        setErrors(newErrors);
        return Object.values(newErrors).every(error => !error);
    }

    const renderField = (
        key: string, label: string, value: string | number, onChange: (val: any) => void,
        type: 'text' | 'number' | 'date', required = false, error?: boolean
    ) => (
        <Field
            key={key}
            label={label}
            value={value ?? ''}
            onChange={onChange}
            type={type}
            required={required}
            error={error}
        />
    );

    const renderSelect = (
        key: string, label: string, value: string | null,
        options: { label: string, value: string }[],
        onChange: (val: string) => void
    ) => (
        <>
            <div className={`${generalClasses['field-block__label']} ${generalClasses['required']}`}>
                {label}
            </div>
            <ReactSelect
                options={options}
                value={findOptionByValue(value, options)}
                onChange={opt => onChange(opt?.value || '')}
                placeholder=""
            />
        </>
    );

    const renderStreetForm = (street: IStreet, isNew = false) => {
        const setField = isNew
            ? (field: string, val: any) => setNewStreet(prev => ({ ...prev as IStreet, [field]: val }))
            : (field: string, val: any) => handleChangeField(field, val, street.id);

        const updateFiles = (file: IFile) => {
            const exists = street.files.some(f => f.id === file.id);
            const updated = exists
                ? street.files.filter(f => f.id !== file.id)
                : [...street.files, file];

            isNew
                ? setNewStreet(prev => ({ ...prev as IStreet, files: updated }))
                : updateStreetFiles(street.id, updated);
        };

        return (
            <div className={`${generalClasses['field-block']} ${generalClasses['field-block--gray']}`} key={street.id}>
                <div>
                    {renderField(`name${suffix}`, t(`${translationKey}.${tab}.name`, lang), street[`name${suffix}`], val => setField(`name${suffix}`, val), 'text', true, parentErrors?.[`${street.id}-name${suffix}`] || errors[`name${suffix}`])}
                    {!isNew && (
                        <button onClick={() => deleteStreetById(street.id)} className={generalClasses['field-block__delete']}>
                            <XIcon />
                        </button>
                    )}
                </div>

                {renderField(
                    'totalStreetLength',
                    t(`${translationKey}.${tab}.totalStreetLength`, lang),
                    street.totalStreetLength ?? '',
                    val => setField('totalStreetLength', val),
                    'number',
                    true,
                    parentErrors?.[`${street.id}-totalStreetLength`] || errors.totalStreetLength
                )}

                {renderSelect(
                    `factCondition-${street.id}`,
                    t(`${translationKey}.${tab}.factCondition`, lang),
                    street.factCondition,
                    conditionOptions(t, 'projects.infrastructure-projects', lang),
                    val => setField('factCondition', val)
                )}

                {renderSelect(
                    `repairRequired-${street.id}`,
                    t(`${translationKey}.${tab}.repairRequired`, lang),
                    street.repairRequired,
                    yesOrNo(t, 'projects.infrastructure-projects', lang),
                    val => setField('repairRequired', val)
                )}

                {street.repairRequired?.toString() === 'true' && (
                    <StreetDocumentation
                        data={data}
                        setData={setFormData}
                        isAddingFund={isAddingFund[street.id] || false}
                        setIsAddingFund={val => setIsAddingFund(prev => ({ ...prev as Record<string, boolean>, [street.id]: val }))}
                        parentErrors={{}}
                        lang={lang}
                        newFund={newFund[street.id]}
                        setNewFund={setNewFund}
                        street={street}
                        setStreet={isNew ? setNewStreet : undefined}
                    />
                )}

                <ImageGrid
                    imageData={convertFilesToImageDate(street.files)}
                    type="STREET"
                    kato={+kato!}
                    objectId={street.id}
                    afterUploadStreet={updateFiles}
                />
            </div>
        );
    };

    if (!kato) return null;

    return (
        <div className={classes[baseClass]}>
            {(
                <div className={classes[`${baseClass}__streets`]}>
                    {!!list.length && list.sort((a, b) => a.id - b.id).map(street => renderStreetForm(street))}

                    {!isAdding && (
                        <div className={generalClasses['field-block__actions']}>
                            <button onClick={handleAddStreet}>
                                <PlusIcon /> {t(`actions.add-street`, lang)}
                            </button>
                        </div>
                    )}

                    {isAdding && newStreet && (
                        <>
                            {renderStreetForm(newStreet, true)}
                            <div className={generalClasses['field-block__actions']}>
                                <button onClick={saveNewStreet}>{t(`actions.save`, lang)}</button>
                                <button onClick={cancelAddStreet}>{t(`actions.cancel`, lang)}</button>
                            </div>
                        </>
                    )}
                </div>
            )}

        </div>
    );
};

export default StreetList;
